import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Drafts as DraftsIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from "react-router-dom";
import api from '../api';
import DOMPurify from 'dompurify';

const AnnouncementsList = ({ role = 'student' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchAnnouncements();
    fetchUnreadCount();
  }, [role]);

  const fetchUnreadCount = async () => {
    try {
      const endpoint = {
        student: 'student/notifications/unread-announcement-count',
        parent: 'parent/notifications/unread-announcement-count',
        tutor: 'tutor/notifications/unread-announcement-count'
      }[role] || 'student/notifications/unread-announcement-count';

      const response = await api.get(endpoint);
      if (response.data && response.data.success) {
        setUnreadCount(response.data.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const endpoint = {
        student: 'student/announcements',
        parent: 'parent/announcements',
        tutor: 'tutor/announcements'
      }[role] || 'student/announcements';
      const response = await api.get(endpoint, {
        params: { current_week: 1 }
      });

      console.log('AnnouncementsList API Response:', response.data);

      let announcementsData = [];
      if (response.data) {
        if (response.data.success && response.data.data) {
          // Tutor returns paginated { data: { data: [...], ... } }; parent/student return { data: [...] }
          const raw = response.data.data;
          if (Array.isArray(raw)) {
            announcementsData = raw;
          } else if (raw && Array.isArray(raw.data)) {
            announcementsData = raw.data;
          }
        } else if (Array.isArray(response.data)) {
          announcementsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          announcementsData = response.data.data;
        }
      }

      setAnnouncements(announcementsData);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setAnnouncements([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAnnouncement = async (announcement) => {
    setSelectedAnnouncement(announcement);
    setDialogOpen(true);

    // Mark as read and reduce unread count
    try {
      const endpoint = {
        student: 'student/notifications/mark-announcement-read',
        parent: 'parent/notifications/mark-announcement-read',
        tutor: 'tutor/notifications/mark-announcement-read'
      }[role] || 'student/notifications/mark-announcement-read';

      const response = await api.post(endpoint, { announcement_id: announcement.id });
      if (response.data?.success && response.data?.data?.count !== undefined) {
        setUnreadCount(response.data.data.count);
      } else if (response.data?.success && unreadCount > 0) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking announcement as read:', error);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAnnouncement(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  };

  if (loading) {
    return (
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
        </CardContent>
      </Card>
    );
  }

  if (announcements.length === 0) {
    return null;
  }

  const getViewAllPath = () => {
    switch (role) {
      case 'student': return '/student/announcements';
      case 'parent': return '/parent/announcements';
      case 'tutor': return '/tutor/announcements';
      default: return '/student/announcements';
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '1px solid #e0e0e0',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0'
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: '#1a3a5f',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            Recent Announcements {unreadCount > 0 && `(Unread: ${unreadCount})`}
          </Typography>
          <Button
            component={RouterLink}
            to={getViewAllPath()}
            size="small"
            variant="outlined"
            sx={{ textTransform: "none" }}
          >
            View All
          </Button>
        </Box>

        {/* Table Container */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#001f3f' }}>
                <TableCell
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    width: isMobile ? '120px' : '160px',
                    textAlign: 'center',
                    py: 1.5
                  }}
                >
                  Date
                </TableCell>
                <TableCell
                  sx={{
                    color: 'white',
                    fontWeight: 700,
                    py: 1.5
                  }}
                >
                  Title
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {announcements.slice(0, 5).map((announcement, index) => (
                <TableRow
                  key={announcement.id}
                  sx={{
                    '&:nth-of-type(even)': { backgroundColor: '#f9f9f9' },
                    '&:hover': { backgroundColor: '#f5f5f5' }
                  }}
                >
                  <TableCell
                    sx={{
                      borderRight: '1px solid #e0e0e0',
                      textAlign: 'center',
                      fontSize: '0.85rem',
                      color: '#444'
                    }}
                  >
                    {formatDate(announcement.created_at)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        sx={{ mr: 1, p: 0.5, color: '#4da8da' }}
                        onClick={() => handleOpenAnnouncement(announcement)}
                      >
                        <EmailIcon fontSize="small" />
                      </IconButton>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#007bff',
                          fontWeight: 500,
                          cursor: 'pointer',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => handleOpenAnnouncement(announcement)}
                      >
                        {announcement.title}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Announcement Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{
          background: '#001f3f',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon />
            <Typography variant="h6" fontWeight={600}>
              Announcement
            </Typography>
          </Box>
          <IconButton
            onClick={handleCloseDialog}
            sx={{
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          {selectedAnnouncement && (
            <Box>
              <Typography variant="h5" sx={{ mb: 2, color: '#1a3a5f', fontWeight: 700 }}>
                {selectedAnnouncement.title}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Typography variant="caption" sx={{ color: '#666', bgcolor: '#f0f4f8', px: 1.5, py: 0.5, borderRadius: 1 }}>
                  Posted on: {formatDate(selectedAnnouncement.created_at)}
                </Typography>
              </Box>

              <Box
                sx={{
                  color: '#333',
                  lineHeight: 1.7,
                  fontSize: '1.05rem',
                  '& p': { mb: 1 },
                  '& p:last-child': { mb: 0 },
                  '& ul, & ol': { pl: 2, my: 1 },
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(String(selectedAnnouncement.message || ''), {
                    USE_PROFILES: { html: true },
                  }),
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid #eee' }}>
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            sx={{
              bgcolor: '#001f3f',
              color: 'white',
              borderRadius: 1,
              px: 4,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#003366',
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnnouncementsList;

